<script lang="ts" setup>
import { reactive, ref, useAuth, useDialog, useI18n, useRoute } from "#imports";
import type { AuthSteps } from "./MPAuth.vue";
import MPInput from "~/components/forms/MPInput.vue";
import MPButton from "~/components/forms/MPButton.vue";
import MPImg from "~/components/widgets/MPImg.vue";
import {
  isEmail,
  isMinStringLength,
  isRequired,
} from "~/components/forms/validation";
import MPMessage from "~/components/widgets/MPMessage.vue";
const { t } = useI18n();

const emit = defineEmits<{
  toggleAuthStep: [value: AuthSteps];
  startOnboarding: [];
}>();

// Login form
const loginModel = reactive({
  email: "",
  password: "",
});

const route = useRoute();
const { closeDialog } = useDialog();
const authStore = useAuth();

const loginFailed = ref(false);
const loginSuccess = ref(false);
const isLoading = ref(false);

const handleLogin = async () => {
  isLoading.value = true;
  try {
    await authStore.signInWithPassword({
      email: loginModel.email,
      password: loginModel.password,
    });

    loginSuccess.value = true;

    await new Promise((resolve) =>
      setTimeout(() => {
        closeDialog();
        resolve(undefined);
      }, 500),
    );
    if (route.path == "/creators/apply") {
      emit("startOnboarding");
    }
  } catch (e) {
    loginFailed.value = true;
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <MPMessage v-if="loginFailed" class="error-message" version="error">
    {{ t("auth.login.loginFailed") }}
  </MPMessage>
  <div class="title">
    <h3>Welcome <span>Back</span></h3>
    <p v-if="!isLoading">Please enter your details</p>
  </div>

  <form v-if="!isLoading" class="login" @submit.prevent="handleLogin">
    <div>
      <MPInput
        v-model="loginModel.email"
        name="email"
        type="email"
        :placeholder="t('auth.login.email')"
        class="input-email"
        :validate="(v) => isRequired(v) || isEmail(v)"
      />
    </div>
    <div>
      <MPInput
        v-model="loginModel.password"
        name="password"
        type="password"
        :placeholder="t('auth.login.password')"
        :validate="(v) => isRequired(v) || isMinStringLength(8)(v)"
      />
    </div>
    <NuxtLinkLocale to="forgot-password" class="forgot-password">
      <small>{{ t("auth.login.forgot") }}</small>
    </NuxtLinkLocale>

    <MPButton
      type="submit"
      :disabled="isLoading"
      :isLoading="isLoading"
      :label="t('auth.login.title')"
    />
    <div class="divider">
      <hr />
      <small>or</small>
      <hr />
    </div>

    <MPButton
      label="Login with Magic Link"
      :disabled="isLoading"
      version="secondary"
      @click="emit('toggleAuthStep', 'magiclink')"
    />
    <div class="social-oauth">
      <MPImg
        src="/media/images/google-social-auth.svg"
        @click="authStore.signInWithOAuth('google')"
      />
    </div>
    <a class="signup-link" @click="emit('toggleAuthStep', 'signup')">
      Don't have an account yet? <span>Join</span>
    </a>
  </form>
  <div v-if="isLoading && !loginSuccess" class="loading-screen">
    <MPImg src="/media/images/authenticating-graphic.svg" />
    <p>Authenticating</p>
  </div>
  <div v-if="isLoading && loginSuccess" class="loading-success">
    <MPImg src="/media/images/authenticated-graphic.svg" />
    <p>You are signed in</p>
  </div>
</template>

<style lang="postcss" scoped>
.login {
  display: grid;
  gap: 1rem;
  padding: 2rem;
}

.title {
  color: var(--color-slate-500);
  h3 {
    margin: 0;
    margin-top: 2rem;
    margin-bottom: 0.5rem;
    span {
      color: var(--color-rose-400);
    }
  }
  p {
    margin: 0;
    padding-inline: 3rem;
    margin-top: 1rem;
    color: var(--color-slate-400);
    line-height: 1.2 !important;
  }
}

.divider {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
  color: var(--color-slate-500);
}

.error-message {
  margin-bottom: 1rem;
}

.social-oauth {
  display: flex;
  gap: 0.5rem;
  margin: auto;
  img {
    cursor: pointer;
  }
}

.signup-link {
  cursor: pointer;
  span {
    color: var(--color-rose-400);
  }
}

.forgot-password {
  display: flex;
  justify-content: end;
  color: var(--color-slate-500);
}

.loading-screen {
  padding: 1rem;
  img {
    margin: auto;
    width: 80%;
  }

  p {
    color: var(--color-rose-400);
  }
}

.loading-success {
  padding: 1rem;
  img {
    margin: auto;
    width: 80%;
  }

  p {
    color: var(--color-green-500);
  }
}
</style>
